import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createApiClient, type ApiClient } from './api/client.js';
import type { User } from './api/types.js';

interface AuthContextValue {
  api: ApiClient;
  user: User | null;
  /** Why the session check failed (API unreachable), if it did. */
  startupError: string | undefined;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthState = { phase: 'checking' } | { phase: 'ready'; user: User | null; error?: string };

export function AuthProvider({
  children,
  fallback,
  signIn,
}: {
  children: ReactNode;
  /** Shown while the session is checked. */
  fallback: ReactNode;
  /** Rendered instead of `children` without a session. */
  signIn: ReactNode;
}) {
  const [state, setState] = useState<AuthState>({ phase: 'checking' });

  const api = useMemo(
    () =>
      createApiClient({
        onUnauthorized: () => {
          setState({ phase: 'ready', user: null });
        },
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    api.me().then(
      (user) => {
        if (!cancelled) setState({ phase: 'ready', user });
      },
      (err: unknown) => {
        if (cancelled) return;
        // The API is unreachable: show the login page with the reason.
        setState({
          phase: 'ready',
          user: null,
          error: err instanceof Error ? err.message : String(err),
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api]);

  const login = useCallback(
    async (email: string, password: string) => {
      const user = await api.login(email, password);
      setState({ phase: 'ready', user });
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setState({ phase: 'ready', user: null });
    }
  }, [api]);

  const user = state.phase === 'ready' ? state.user : null;
  const startupError = state.phase === 'ready' ? state.error : undefined;
  const value = useMemo(
    () => ({ api, user, startupError, login, logout }),
    [api, user, startupError, login, logout],
  );

  let content: ReactNode;
  if (state.phase === 'checking') content = fallback;
  else if (!state.user) content = signIn;
  else content = children;
  return <AuthContext value={value}>{content}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** The API client of the signed-in session. */
export function useApi(): ApiClient {
  return useAuth().api;
}
